import type { File } from "$lib/wasm/file";
import { wasm } from "$lib/wasm/wasm";
import { createDeferredPromise } from "ceyper-common";
import { getCanvas } from "$lib/image/preview";
import { applyPalette, GIFEncoder, quantize, type GifPalette } from "gifenc";

export type GifExportOptions = {
    name: string;
    scale: number;
    loop: boolean;
    loopCount: number;
    alphaMode: "transparent-threshold" | "matte-color";
    alphaThreshold: number;
    matteColor: string;
    dither: boolean;
    quality: "fast" | "balanced" | "best";
};

export async function downloadFileFrame(
    file: File,
    options: { name: string; scale: number; frame: number }
) {
    const imageData = renderFileFrame(file, options.frame, options.scale);
    const htmlCanvas = document.createElement("canvas");
    htmlCanvas.width = imageData.width;
    htmlCanvas.height = imageData.height;
    const ctx = htmlCanvas.getContext("2d")!;
    ctx.putImageData(imageData, 0, 0);

    const blob = await htmlCanvasBlobAsync(htmlCanvas, "image/png");
    downloadBlob(blob, `${options.name}.png`);
}

export async function downloadFileAllFrames(file: File, options: { name: string; scale: number }) {
    const htmlCanvas = document.createElement("canvas");
    htmlCanvas.width = file.width * options.scale;
    htmlCanvas.height = file.height * options.scale;
    const ctx = htmlCanvas.getContext("2d")!;

    const files = [] as { name: string; blob: Blob }[];

    for (let frame = 0; frame < file.getFrames().length; frame++) {
        ctx.putImageData(renderFileFrame(file, frame, options.scale), 0, 0);

        const blob = await htmlCanvasBlobAsync(htmlCanvas, "image/png");
        files.push({
            name: `${options.name}_${frame}.png`,
            blob,
        });
    }

    await downloadZip(files, options.name);
}

export async function downloadFileGif(file: File, options: GifExportOptions) {
    const frames = file.getFrames();
    const rendered = frames.map((frame, index) => ({
        imageData: renderFileFrame(file, index, options.scale),
        duration: frame.duration,
    }));

    const width = rendered[0]?.imageData.width ?? file.width * options.scale;
    const height = rendered[0]?.imageData.height ?? file.height * options.scale;
    const preparedFrames = rendered.map(({ imageData, duration }) => ({
        data: prepareGifPixels(imageData.data, options),
        duration,
    }));

    const paletteData = new Uint8Array(width * height * 4 * preparedFrames.length);
    preparedFrames.forEach((frame, index) => {
        paletteData.set(frame.data, index * width * height * 4);
    });

    const transparent = options.alphaMode === "transparent-threshold" && hasTransparentPixels(paletteData);
    const format = transparent ? "rgba4444" : qualityFormat(options.quality);
    const palette = quantize(paletteData, maxColorsForQuality(options.quality), {
        format,
        oneBitAlpha: transparent ? options.alphaThreshold : false,
        clearAlpha: transparent,
        clearAlphaThreshold: options.alphaThreshold,
        clearAlphaColor: 0x00,
    });

    const transparentIndex = transparent ? ensureTransparentColor(palette) : -1;
    const gif = GIFEncoder({
        initialCapacity: Math.max(4096, width * height * preparedFrames.length),
    });

    preparedFrames.forEach((frame, index) => {
        const indexed = options.dither
            ? applyPaletteDithered(frame.data, width, height, palette, format, transparentIndex)
            : applyPalette(frame.data, palette, format);

        gif.writeFrame(indexed, width, height, {
            palette: index === 0 ? palette : undefined,
            delay: frame.duration,
            repeat: index === 0 ? (options.loop ? options.loopCount : -1) : undefined,
            transparent,
            transparentIndex,
            dispose: transparent ? 2 : -1,
        });
    });

    gif.finish();
    const bytes = gif.bytes();
    const buffer = bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) as ArrayBuffer;
    downloadBlob(new Blob([buffer], { type: "image/gif" }), `${options.name}.gif`);
}

function renderFileFrame(file: File, frame: number, scale: number) {
    const canvas = getCanvas(file);
    const target = new ImageData(file.width * scale, file.height * scale);
    const target32 = new Uint32Array(target.data.buffer);
    const source32 = new Uint32Array(wasm.memory.buffer, canvas.data_ptr, file.width * file.height);

    canvas.draw_file_frame(file.internal, frame);
    for (let i = 0; i < target32.length; i++) {
        const x = i % target.width;
        const y = Math.floor(i / target.width);
        const sourceX = Math.floor(x / scale);
        const sourceY = Math.floor(y / scale);
        const sourceIndex = sourceY * file.width + sourceX;
        target32[i] = source32[sourceIndex];
    }

    return target;
}

function prepareGifPixels(source: Uint8ClampedArray, options: GifExportOptions) {
    const output = new Uint8Array(source);

    if (options.alphaMode === "matte-color") {
        const matte = parseHexColor(options.matteColor);
        for (let i = 0; i < output.length; i += 4) {
            const alpha = output[i + 3] / 255;
            output[i] = Math.round(output[i] * alpha + matte.r * (1 - alpha));
            output[i + 1] = Math.round(output[i + 1] * alpha + matte.g * (1 - alpha));
            output[i + 2] = Math.round(output[i + 2] * alpha + matte.b * (1 - alpha));
            output[i + 3] = 255;
        }
    } else {
        for (let i = 0; i < output.length; i += 4) {
            if (output[i + 3] < options.alphaThreshold) {
                output[i] = 0;
                output[i + 1] = 0;
                output[i + 2] = 0;
                output[i + 3] = 0;
            } else {
                output[i + 3] = 255;
            }
        }
    }

    return output;
}

function parseHexColor(value: string) {
    const hex = value.replace(/^#/, "").padEnd(6, "0").slice(0, 6);
    return {
        r: parseInt(hex.slice(0, 2), 16),
        g: parseInt(hex.slice(2, 4), 16),
        b: parseInt(hex.slice(4, 6), 16),
    };
}

function qualityFormat(quality: GifExportOptions["quality"]) {
    return quality === "fast" ? "rgb444" : "rgb565";
}

function maxColorsForQuality(quality: GifExportOptions["quality"]) {
    if (quality === "fast") return 128;
    if (quality === "balanced") return 192;
    return 256;
}

function hasTransparentPixels(data: Uint8Array) {
    for (let i = 3; i < data.length; i += 4) {
        if (data[i] === 0) return true;
    }
    return false;
}

function ensureTransparentColor(palette: GifPalette) {
    const index = palette.findIndex((color) => (color[3] ?? 255) === 0);
    if (index !== -1) return index;

    if (palette.length < 256) {
        palette.push([0, 0, 0, 0]);
        return palette.length - 1;
    }

    palette[palette.length - 1] = [0, 0, 0, 0];
    return palette.length - 1;
}

function applyPaletteDithered(
    source: Uint8Array,
    width: number,
    height: number,
    palette: GifPalette,
    format: "rgb565" | "rgb444" | "rgba4444",
    transparentIndex: number
) {
    if (format === "rgba4444") {
        return applyPalette(source, palette, format);
    }

    const data = Float32Array.from(source);
    const indexed = new Uint8Array(width * height);

    for (let y = 0; y < height; y++) {
        for (let x = 0; x < width; x++) {
            const pixel = (y * width + x) * 4;
            const nearest = nearestPaletteIndex(data[pixel], data[pixel + 1], data[pixel + 2], palette);
            const color = palette[nearest];
            indexed[y * width + x] = nearest;

            const errorR = data[pixel] - color[0];
            const errorG = data[pixel + 1] - color[1];
            const errorB = data[pixel + 2] - color[2];

            diffuseError(data, width, height, x + 1, y, errorR, errorG, errorB, 7 / 16);
            diffuseError(data, width, height, x - 1, y + 1, errorR, errorG, errorB, 3 / 16);
            diffuseError(data, width, height, x, y + 1, errorR, errorG, errorB, 5 / 16);
            diffuseError(data, width, height, x + 1, y + 1, errorR, errorG, errorB, 1 / 16);
        }
    }

    if (transparentIndex >= 0) {
        for (let i = 3; i < source.length; i += 4) {
            if (source[i] === 0) indexed[(i - 3) / 4] = transparentIndex;
        }
    }

    return indexed;
}

function nearestPaletteIndex(r: number, g: number, b: number, palette: GifPalette) {
    let best = 0;
    let bestDistance = Infinity;

    for (let i = 0; i < palette.length; i++) {
        const color = palette[i];
        const distance = square(r - color[0]) + square(g - color[1]) + square(b - color[2]);
        if (distance < bestDistance) {
            best = i;
            bestDistance = distance;
        }
    }

    return best;
}

function diffuseError(
    data: Float32Array,
    width: number,
    height: number,
    x: number,
    y: number,
    r: number,
    g: number,
    b: number,
    factor: number
) {
    if (x < 0 || y < 0 || x >= width || y >= height) return;

    const pixel = (y * width + x) * 4;
    data[pixel] = clampByte(data[pixel] + r * factor);
    data[pixel + 1] = clampByte(data[pixel + 1] + g * factor);
    data[pixel + 2] = clampByte(data[pixel + 2] + b * factor);
}

function square(value: number) {
    return value * value;
}

function clampByte(value: number) {
    return Math.max(0, Math.min(255, value));
}

function htmlCanvasBlobAsync(canvas: HTMLCanvasElement, type?: string) {
    const [promise, resolve, reject] = createDeferredPromise<Blob>();
    canvas.toBlob(async (blob) => {
        if (!blob) return reject();
        resolve(blob);
    }, type);
    return promise;
}

async function downloadZip(files: { name: string; blob: Blob }[], name: string) {
    const { default: JSZip } = await import("jszip");
    const zip = new JSZip();

    for (let { name, blob } of files) {
        zip.file(name, blob);
    }

    const zipBlob = await zip.generateAsync({ type: "blob" });
    downloadBlob(zipBlob, `${name}.zip`);
}

function downloadBlob(blob: Blob, name: string) {
    const urlBlob = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.download = name;
    link.href = urlBlob;
    link.click();
    URL.revokeObjectURL(urlBlob);
}
